/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
/* eslint-disable @typescript-eslint/no-unsafe-return */

import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import { isFieldSchema, isSectionSchema } from '../utils';
import { FieldOptionT, FormFieldT, FormItemT, FormSectionT, TextWithTranslationsT } from '../types';
import { ComponentInputTypeT } from '../schemas/types';

// --- Types ---

export interface ProcessedFollowUpField {
  id: string;
  inputType: ComponentInputTypeT;
  order?: number;
  isFollowUp: true;
  optionId?: string | null;
  config?: any;
  label?: TextWithTranslationsT | null;
  text?: TextWithTranslationsT | null;
  field?: string;
  isInfo?: boolean;
  optionList?: Omit<FieldOptionT, 'key'>[] | null;
  followUpQuestionList?: (ProcessedFollowUpField | ProcessedFollowUpSection)[] | null;
  min?: number | null;
  max?: number | null;
  link?: TextWithTranslationsT | null;
  parent?: string | null;
  parentSection?: string | null;
  parentSectionTitle?: TextWithTranslationsT | null;
  section?: string | null;
  sectionTitle?: TextWithTranslationsT | null;
}

export interface ProcessedFollowUpSection {
  id: string;
  section: TextWithTranslationsT | string | number;
  isAdvanced?: boolean;
  fields: (ProcessedFollowUpField | ProcessedFollowUpSection)[];
}

// --- Configuration Constants ---

const BASE_OMIT_KEYS = [
  'timestamp',
  'hintText',
  'validations',
  'required',
  'previewPlaceholders',
  'disabledId',
  'disabled',
  'isFrontEndOnly',
  'hideFieldSelector',
  'config',
];

const FOLLOW_UP_OMIT_KEYS = [...BASE_OMIT_KEYS, 'documentReview', 'enableWorkPermit', 'section', 'parentSection'];

/**
 * Main function to transform a raw FormItem array into a flattened,
 * language-filtered FormField array.
 */
export function generateFormSchema(
  formItemsInput: FormItemT[],
  language: string,
  forValidation = false,
  selectedLanguages: string[] = ['en'],
): FormFieldT[] {
  // --- Helper: Language Filtering ---
  /** Filters translation objects to only include keys present in selectedLanguages */
  const filterLanguages = (data: TextWithTranslationsT | undefined): TextWithTranslationsT | null => {
    if (!data) return null;
    const result: TextWithTranslationsT = {};
    for (const lang of selectedLanguages) {
      if (Object.prototype.hasOwnProperty.call(data, lang)) {
        result[lang] = data[lang];
      }
    }
    return result;
  };

  // --- Helper: Options Processing ---
  /** Removes internal keys and filters languages for option lists */
  const processOptions = (options?: FieldOptionT[] | null): Omit<FieldOptionT, 'key'>[] | null => {
    if (!options || options.length === 0) return null;
    return options.map((option) => ({
      ...(omit(option, ['key']) as Omit<FieldOptionT, 'key'>),
      text: filterLanguages(option.text)!,
    }));
  };

  // --- Helper: Recursive Follow-up Processing ---
  /** Recursively processes questions nested inside other questions (yes/no follow-ups) */
  function processFollowUps(
    items: (FormFieldT | FormSectionT)[] | null | undefined,
  ): ProcessedFollowUpField[] | null | undefined {
    if (!items || items.length === 0) return null;

    return items
      .filter((item): item is FormItemT => {
        if (!item) return false;
        if (isFieldSchema(item)) {
          // Exclude disabled fields or front-end only fields if not in validation mode
          return !(item.disabled || (item.isFrontEndOnly && !forValidation));
        }
        return isSectionSchema(item);
      })
      .flatMap((item) => {
        if (isFieldSchema(item)) {
          const field: ProcessedFollowUpField = {
            ...omit(item, FOLLOW_UP_OMIT_KEYS),
            id: item.id,
            inputType: item.inputType,
            order: item.order,
            isFollowUp: true,
            optionId: item.optionId,
            config: item.config,
            label: filterLanguages(item.label),
            text: filterLanguages(item.text),
            field: item?.field ?? item.inputType,
            isInfo: item?.isInfo ?? false,
            optionList: processOptions(item.optionList),
            followUpQuestionList: processFollowUps(item.followUpQuestionList),
            min: item.min,
            max: item.max,
            link: filterLanguages(item.link!),
            parent: item.parent,
            parentSection: item.parentSection,
            section: item.section,
            sectionTitle: filterLanguages(item.sectionTitle),
            parentSectionTitle: item.parentSectionTitle ? filterLanguages(item.parentSectionTitle) : null,
          };
          return [field];
        }

        if (isSectionSchema(item)) {
          // If a section is found inside follow-ups, flatten its fields
          return processFollowUps(item.fields) || [];
        }

        return [];
      })
      .filter(Boolean) as ProcessedFollowUpField[];
  }

  // --- Step 1: Filter and Flatten the Input Schema ---
  const flattenedFields = formItemsInput
    .filter((item): item is FormItemT => {
      if (!item) return false;
      if (isFieldSchema(item)) {
        return !(item.disabled || (item.isFrontEndOnly && !forValidation));
      }
      return isSectionSchema(item);
    })
    .flatMap((formItem) => {
      if (isFieldSchema(formItem)) {
        // Handle Document Review specific translations
        const documentReviewProcessed = formItem.documentReview
          ? {
              acknowledgeContent: filterLanguages(formItem.documentReview.acknowledgeContent),
              confirmText: filterLanguages(formItem.documentReview.confirmText),
              description: filterLanguages(formItem.documentReview.description),
              url: filterLanguages(formItem.documentReview.url),
            }
          : null;

        // Process nested follow-up questions
        const processedFollowUps = processFollowUps(formItem.followUpQuestionList)?.flat();

        const outputField: any = {
          ...omit(formItem, BASE_OMIT_KEYS),
          id: formItem.id,
          inputType: formItem.inputType,
          order: 0, // Placeholder, will be updated in Step 2
          label: filterLanguages(formItem.label),
          text: filterLanguages(formItem.text),
          isFollowUp: formItem.isFollowUp || false,
          optionId: formItem.optionId,
          config: formItem.config,
          documentReview: documentReviewProcessed,
          field: formItem?.field ?? formItem.inputType,
          optionList: processOptions(formItem.optionList),
          followUpQuestionList: processedFollowUps,
          section: formItem.section,
          sectionTitle: filterLanguages(formItem.sectionTitle),
          parentSectionTitle: formItem.parentSectionTitle ? filterLanguages(formItem.parentSectionTitle) : null,
        };

        return [outputField];
      }

      if (isSectionSchema(formItem)) {
        // Recursive call to handle nested sections by flattening their fields
        return generateFormSchema(formItem.fields, language, forValidation, selectedLanguages);
      }

      return [];
    });

  // --- Step 2: Final Cleanup and Sequencing ---
  return flattenedFields.map((fieldItem, index) => {
    // Remove all null/undefined properties and assign sequence order
    const cleanedItem = omitBy(fieldItem, isNil) as FormFieldT;
    return {
      ...cleanedItem,
      order: index + 1,
    };
  });
}
